import * as React from 'react'
import type { DateRange } from 'react-day-picker'
import {
 addMonths,
 endOfMonth,
 endOfWeek,
 startOfMonth,
 startOfWeek,
 subDays,
 subMonths,
 subWeeks,
} from 'date-fns'
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { dateToLocalDateString, formatDateLabelStable, todayLocalDateString } from 'helpers/dateTime'
import { normalizeAppLanguage, type AppLanguage } from 'helpers/documentLocale'
import { cn } from '../lib/utils'
import { Button } from './ui/button'
import { Calendar } from './ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'

export type DateRangeValue = { from: string | null; to: string | null }

type Props = {
 id?: string
 value: DateRangeValue
 onChange: (value: DateRangeValue) => void
 placeholder?: string
 disabled?: boolean
 clearable?: boolean
 includeAllTimePreset?: boolean
 className?: string
 'aria-label'?: string
}

type PresetDef = {
 id: string
 label: string
 getRange: () => { from: Date; to: Date }
}

function parseYmdLocal(ymd: string): Date {
 const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim())
 if (!m) return new Date(ymd)
 return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

function normalizeDateRangeValue(value: DateRangeValue): DateRangeValue {
 if (value.from && value.to && value.from > value.to) {
  return { from: value.to, to: value.from }
 }

 return value
}

function weekStartsOn(lang: AppLanguage): 0 | 1 | 6 {
 return lang === 'ar' ? 6 : 1
}

function buildPresets(lang: AppLanguage, t: (key: string) => string): PresetDef[] {
 const today = () => parseYmdLocal(todayLocalDateString())
 const weekOpts = { weekStartsOn: weekStartsOn(lang) }

 return [
  {
   id: 'today',
   label: t('ui.dateRange.presets.today'),
   getRange: () => {
    const d = today()
    return { from: d, to: d }
   },
  },
  {
   id: 'yesterday',
   label: t('ui.dateRange.presets.yesterday'),
   getRange: () => {
    const d = subDays(today(), 1)
    return { from: d, to: d }
   },
  },
  {
   id: 'last7Days',
   label: t('ui.dateRange.presets.last7Days'),
   getRange: () => ({ from: subDays(today(), 6), to: today() }),
  },
  {
   id: 'last30Days',
   label: t('ui.dateRange.presets.last30Days'),
   getRange: () => ({ from: subDays(today(), 29), to: today() }),
  },
  {
   id: 'thisWeek',
   label: t('ui.dateRange.presets.thisWeek'),
   getRange: () => ({ from: startOfWeek(today(), weekOpts), to: today() }),
  },
  {
   id: 'lastWeek',
   label: t('ui.dateRange.presets.lastWeek'),
   getRange: () => {
    const prevWeek = subWeeks(today(), 1)
    return {
     from: startOfWeek(prevWeek, weekOpts),
     to: endOfWeek(prevWeek, weekOpts),
    }
   },
  },
  {
   id: 'thisMonth',
   label: t('ui.dateRange.presets.thisMonth'),
   getRange: () => ({ from: startOfMonth(today()), to: today() }),
  },
  {
   id: 'lastMonth',
   label: t('ui.dateRange.presets.lastMonth'),
   getRange: () => {
    const prev = subMonths(today(), 1)
    return { from: startOfMonth(prev), to: endOfMonth(prev) }
   },
  },
  {
   id: 'last90Days',
   label: t('ui.dateRange.presets.last90Days'),
   getRange: () => ({ from: subDays(today(), 89), to: today() }),
  },
 ]
}

function toDateRange(value: DateRangeValue): DateRange | undefined {
 const normalizedValue = normalizeDateRangeValue(value)
 if (!normalizedValue.from && !normalizedValue.to) return undefined
 return {
  from: normalizedValue.from ? parseYmdLocal(normalizedValue.from) : undefined,
  to: normalizedValue.to ? parseYmdLocal(normalizedValue.to) : undefined,
 }
}

function fromDateRange(range: DateRange | undefined): DateRangeValue {
 if (!range?.from && !range?.to) return { from: null, to: null }
 return normalizeDateRangeValue({
  from: range?.from ? dateToLocalDateString(range.from) : null,
  to: range?.to ? dateToLocalDateString(range.to) : null,
 })
}

function formatRangeAriaLabel(
 value: DateRangeValue,
 t: (key: string, opts?: { from?: string; to?: string }) => string
): string {
 const normalizedValue = normalizeDateRangeValue(value)
 if (normalizedValue.from && normalizedValue.to) {
  return t('ui.dateRange.displayFromTo', {
   from: formatDateLabelStable(normalizedValue.from),
   to: formatDateLabelStable(normalizedValue.to),
  })
 }
 if (normalizedValue.from) return formatDateLabelStable(normalizedValue.from)
 if (normalizedValue.to) return formatDateLabelStable(normalizedValue.to)
 return ''
}

const dateLabelClass =
 'inline-block font-semibold text-foreground tabular-nums [unicode-bidi:isolate]'

function RangeLabelDisplay({
 value,
 rtl,
 t,
}: {
 value: DateRangeValue
 rtl: boolean
 t: (key: string, opts?: { from?: string; to?: string }) => string
}) {
 const normalizedValue = normalizeDateRangeValue(value)
 if (normalizedValue.from && normalizedValue.to) {
  const fromLabel = formatDateLabelStable(normalizedValue.from)
  const toLabel = formatDateLabelStable(normalizedValue.to)
  if (rtl) {
   return (
    <>
     <span>{t('ui.dateRange.displayFromPrefix')}{' '}</span>
     <span dir="ltr" className={dateLabelClass}>
      {fromLabel}
     </span>
     <span>{' '}{t('ui.dateRange.displayToPrefix')}{' '}</span>
     <span dir="ltr" className={dateLabelClass}>
      {toLabel}
     </span>
    </>
   )
  }
  return (
   <span dir="ltr" className={cn(dateLabelClass, 'font-semibold')}>
    {fromLabel}
    <span className="mx-1 font-normal text-muted-foreground">—</span>
    {toLabel}
   </span>
  )
 }

 const single = normalizedValue.from || normalizedValue.to
 if (single) {
  return (
   <span dir="ltr" className={dateLabelClass}>
    {formatDateLabelStable(single)}
   </span>
  )
 }

 return null
}

function rangesEqual(a: DateRangeValue, b: DateRangeValue): boolean {
 return a.from === b.from && a.to === b.to
}

const presetChipClass =
 'flex h-11 w-full items-center rounded-xl border px-4 text-sm font-medium transition-all duration-150 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:ring-offset-1'

function isAllTimeValue(value: DateRangeValue, includeAllTimePreset?: boolean): boolean {
 return Boolean(includeAllTimePreset && !value.from && !value.to)
}

export function DateRangePicker({
 id,
 value,
 onChange,
 placeholder,
 disabled,
 className,
 clearable = true,
 includeAllTimePreset = true,
 'aria-label': ariaLabel,
}: Props) {
 const { t, i18n } = useTranslation()
 const [open, setOpen] = React.useState(false)
 const { from: valueFrom, to: valueTo } = value
 const normalizedValue = normalizeDateRangeValue({ from: valueFrom, to: valueTo })
 const [draftRange, setDraftRange] = React.useState<DateRangeValue>(normalizedValue)
 const [month, setMonth] = React.useState<Date>(() => {
  const range = toDateRange(normalizedValue)
  return range?.from ?? range?.to ?? new Date()
 })
 const [twoMonths, setTwoMonths] = React.useState(false)
 const lang = normalizeAppLanguage(i18n.language)
 const rtl = lang === 'ar'
 const resolvedPlaceholder = placeholder ?? t('ui.dateRange.placeholder')
 const allTimeLabel = t('ui.dateRange.presets.all', { defaultValue: t('reports.common.all') })
 const displayValue = open ? draftRange : normalizedValue
 const isAllTime = isAllTimeValue(normalizedValue, includeAllTimePreset)
 const isDisplayAllTime = isAllTimeValue(displayValue, includeAllTimePreset)
 const hasValue = Boolean(normalizedValue.from || normalizedValue.to) || isAllTime
 const hasDisplayValue = Boolean(displayValue.from || displayValue.to) || isDisplayAllTime
 const draftSelected = toDateRange(draftRange)
 const canApply = Boolean(draftRange.from && draftRange.to)
 const monthCount = twoMonths ? 2 : 1

 const presets = React.useMemo(() => buildPresets(lang, t), [lang, t])

 React.useEffect(() => {
  if (typeof window === 'undefined') return
  const mq = window.matchMedia('(min-width: 640px)')
  const sync = () => setTwoMonths(mq.matches)
  sync()
  mq.addEventListener('change', sync)
  return () => mq.removeEventListener('change', sync)
 }, [])

 React.useEffect(() => {
  if (!open) setDraftRange(normalizeDateRangeValue({ from: valueFrom, to: valueTo }))
 }, [open, valueFrom, valueTo])

 const handleOpenChange = (next: boolean) => {
  if (next) {
   setDraftRange(normalizedValue)
   const range = toDateRange(normalizedValue)
   setMonth(range?.from ?? range?.to ?? new Date())
  }
  setOpen(next)
 }

 const applyDraft = (next: DateRangeValue) => {
  onChange(normalizeDateRangeValue(next))
  setOpen(false)
 }

 const applyPreset = (from: Date, to: Date) => {
  const next = {
   from: dateToLocalDateString(from),
   to: dateToLocalDateString(to),
  }
  setDraftRange(next)
  applyDraft(next)
 }

 const applyAllTime = () => {
  const next = { from: null, to: null }
  setDraftRange(next)
  applyDraft(next)
 }

 const activePresetId = React.useMemo(() => {
  if (includeAllTimePreset && !draftRange.from && !draftRange.to) return 'all'
  for (const preset of presets) {
   const { from, to } = preset.getRange()
   const candidate = {
    from: dateToLocalDateString(from),
    to: dateToLocalDateString(to),
   }
   if (rangesEqual(draftRange, candidate)) return preset.id
  }
  return null
 }, [draftRange, includeAllTimePreset, presets])

 const hintMessage = isAllTimeValue(draftRange, includeAllTimePreset)
  ? allTimeLabel
  : !draftRange.from
  ? t('ui.dateRange.hint.selectStart')
  : !draftRange.to
   ? t('ui.dateRange.hint.selectEnd')
   : t('ui.dateRange.hint.ready')

 const goPrevMonth = () => setMonth((current) => subMonths(current, monthCount))
 const goNextMonth = () => setMonth((current) => addMonths(current, monthCount))

 const ariaRangeLabel = isDisplayAllTime
  ? allTimeLabel
  : hasDisplayValue
   ? formatRangeAriaLabel(displayValue, t)
   : undefined

 return (
  <Popover open={open} onOpenChange={handleOpenChange}>
   <PopoverTrigger asChild>
    <button
     id={id}
     type="button"
     disabled={disabled}
     dir={rtl ? 'rtl' : 'ltr'}
     aria-label={ariaLabel ?? ariaRangeLabel ?? resolvedPlaceholder}
     aria-expanded={open}
     className={cn(
      'flex h-10 min-h-10 w-full items-center rounded-xl border px-3 py-2 text-sm shadow-sm transition-all',
      'text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0',
      hasValue
       ? 'border-[hsl(var(--primary-selection-border))] bg-[hsl(var(--primary-soft-surface))]/40 hover:bg-[hsl(var(--primary-soft-surface))]/55'
       : 'border-border-subtle bg-background hover:bg-muted/40',
      open && 'ring-2 ring-[hsl(var(--primary))]/25',
      'focus-visible:ring-[hsl(var(--primary))]/30',
      disabled && 'cursor-not-allowed opacity-60',
      className
     )}
    >
     <CalendarIcon
      className={cn(
       'pointer-events-none me-2 h-4 w-4 shrink-0',
       hasValue || open ? 'text-[hsl(var(--primary))]' : 'text-muted-foreground'
      )}
      aria-hidden
     />
     <span className={cn('min-w-0 flex-1 truncate', !hasDisplayValue && 'font-normal text-muted-foreground')}>
      {isDisplayAllTime ? (
       <span className="font-semibold text-foreground">{allTimeLabel}</span>
      ) : hasDisplayValue ? (
       <RangeLabelDisplay value={displayValue} rtl={rtl} t={t} />
      ) : (
       resolvedPlaceholder
      )}
     </span>
     {clearable && hasDisplayValue && !isDisplayAllTime && !disabled && !open ? (
      <span
       role="button"
       tabIndex={0}
       className="ms-2 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
       aria-label={t('ui.dateRange.presets.clear')}
       onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onChange({ from: null, to: null })
       }}
       onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
         e.preventDefault()
         e.stopPropagation()
         onChange({ from: null, to: null })
        }
       }}
      >
       <X className="h-4 w-4" />
      </span>
     ) : null}
    </button>
   </PopoverTrigger>
   <PopoverContent
    side="bottom"
    align={rtl ? 'end' : 'start'}
    collisionPadding={12}
    className="date-range-picker-popover z-[110000] w-auto max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-2xl border-border p-0 shadow-premium-lg"
    dir={rtl ? 'rtl' : 'ltr'}
   >
    <div className="flex flex-col sm:flex-row">
     <div className="date-range-picker-presets border-b border-border-subtle bg-primary-soft/30 p-4 sm:w-60 sm:border-b-0 sm:border-e sm:border-primary-soft-border/40">
      <p className="mb-3 text-sm font-bold text-foreground">
       {t('ui.dateRange.presets.title')}
      </p>
      <div className="flex max-h-[min(20rem,52vh)] flex-col gap-2 overflow-y-auto overscroll-contain">
       {presets.map((preset) => {
        const { from, to } = preset.getRange()
        const isActive = activePresetId === preset.id
        return (
         <button
          key={preset.id}
          type="button"
          className={cn(
           presetChipClass,
           isActive
            ? 'border-primary bg-primary font-semibold text-primary-foreground shadow-sm'
            : 'border-primary-soft-border/70 bg-card text-foreground shadow-sm hover:border-primary/35 hover:bg-primary-soft/80 active:scale-[0.99]'
          )}
          onClick={() => applyPreset(from, to)}
         >
          {preset.label}
         </button>
        )
       })}
       {includeAllTimePreset ? (
        <button
         type="button"
         className={cn(
          presetChipClass,
          activePresetId === 'all'
           ? 'border-primary bg-primary font-semibold text-primary-foreground shadow-sm'
           : 'border-primary-soft-border/70 bg-card text-foreground shadow-sm hover:border-primary/35 hover:bg-primary-soft/80 active:scale-[0.99]'
         )}
         onClick={applyAllTime}
        >
         {allTimeLabel}
        </button>
       ) : null}
      </div>
     </div>

     <div className="flex min-w-0 flex-1 flex-col">
      <div className="date-range-picker-toolbar flex items-center justify-between gap-2 border-b border-border-subtle bg-muted/30 px-3 py-2.5">
       <p className="date-range-picker-hint flex min-w-0 flex-1 items-center gap-2 text-sm font-medium text-foreground/90">
        <span
         className="h-2 w-2 shrink-0 rounded-full bg-[hsl(var(--primary))]"
         aria-hidden
        />
        <span className="truncate">{hintMessage}</span>
       </p>
       <div className="flex shrink-0 items-center gap-1">
        <Button
         type="button"
         variant="outline"
         size="icon-sm"
         className="h-8 w-8 border-[hsl(var(--primary-selection-border))]/50 hover:bg-[hsl(var(--primary-soft-surface))]"
         aria-label={t('ui.dateRange.navPrevious')}
         onClick={goPrevMonth}
        >
         {rtl ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
        <Button
         type="button"
         variant="outline"
         size="icon-sm"
         className="h-8 w-8 border-[hsl(var(--primary-selection-border))]/50 hover:bg-[hsl(var(--primary-soft-surface))]"
         aria-label={t('ui.dateRange.navNext')}
         onClick={goNextMonth}
        >
         {rtl ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </Button>
       </div>
      </div>
      <div className="bg-card p-3">
       <Calendar
        mode="range"
        month={month}
        onMonthChange={setMonth}
        numberOfMonths={monthCount}
        hideNavigation
        pagedNavigation={monthCount > 1}
        selected={draftSelected}
        onSelect={(range) => {
         const next = normalizeDateRangeValue(fromDateRange(range))
         setDraftRange(next)
         if (next.from) setMonth(parseYmdLocal(next.from))
        }}
       />
      </div>
      <div
       className={cn(
        'date-range-picker-footer flex items-center gap-2 border-t border-border-subtle bg-muted/20 px-3 py-2.5',
        rtl ? 'flex-row-reverse justify-start' : 'justify-end'
       )}
      >
       <Button type="button" variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setOpen(false)}>
        {t('ui.cancel')}
       </Button>
       <Button
        type="button"
        size="sm"
        disabled={!canApply}
        className="min-w-[5.5rem] bg-primary font-semibold text-primary-foreground hover:bg-primary-hover"
        onClick={() => applyDraft(draftRange)}
       >
        {t('ui.dateRange.apply')}
       </Button>
      </div>
     </div>
    </div>
   </PopoverContent>
  </Popover>
 )
}
